/*
 *  Campaign__Create_ChannelLinkEditor.jsx
 *
 *
 *
 */
//==============================================================================
// External dep.
//==============================================================================
import React, { useState, useCallback, useMemo, useEffect } from "react";
import styled from "styled-components";
import ChannelDataEditor from "./Campaign__Create_ChannelDataEditor.jsx";
import _ from "lodash";
//------------------------------------------------------------------------------
// Internal dep.
//------------------------------------------------------------------------------
import Modal from "../../../Components/Modal";
import usePrevious from "../../../hooks/usePrevious.jsx";
import Actions from "../../../Actions";
import { withRefluxStore, PreviewStore } from "../../../Stores";
import { channelsFieldsByShortName } from "../../../data/app__channelFields";
import logger from "../../../utils/LoggerFrontend.js";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "react-loading-indicator";
import i18next from "i18next";
//------------------------------------------------------------------------------
// Styled Components
//------------------------------------------------------------------------------
const ChannelLinkEditorWrapper = styled.div`
  display: block;
  max-width: 465px;
  margin-top: 20px;
  text-align: left;
  color: #bdbdbd;
`;
const CHANNELS_WITH_AUTO_LINK_UPDATES = [
  "facebook",
  "instagram",
  "google-display",
  "google-search",
  "youtube",
  "ebay-kleinanzeigen",
  "snapchat",
  "spotify",
  "tiktok",
  "twitter",
  "pinterest",
  "linkedin-feed",
  "reddit",
  "linkedin",
  "indeed"
];

const CHANNELS_WITH_XML_FEED_CAMPAIGN_CREATION = ["linkedin", "indeed"];

const parseResultToMessage = ({ status, message, channelCampaignId, channel }) => {
  logger.debug("ChannelLinkEditor:parseResultToMessage", "called", {
    status,
    message,
    channelCampaignId,
    channel
  });
  if (status === "SUCCESS") {
    if (CHANNELS_WITH_XML_FEED_CAMPAIGN_CREATION.includes(channel)) {
      return (
        <>
          <span>✅</span>
          <span
            style={{
              display: "inline-block",
              marginLeft: "12px",
              width: "200px",
              fontWeight: "600",
              textTransform: "capitalize"
            }}
          >
            {channel}{" "}
          </span>
          <span>
            {i18next.t(
              "The URL has been updated correctly. Changes may take up to 6 hours to propagate across the platform."
            )}
          </span>
        </>
      );
    } else {
      return (
        <span>
          <span>✅</span>
          <span
            style={{
              display: "inline-block",
              marginLeft: "12px",
              width: "200px",
              fontWeight: "600",
              textTransform: "capitalize"
            }}
          >
            {channel}{" "}
          </span>
        </span>
      );
    }
  } else {
    return (
      <>
        <span>⛔</span>
        <span
          style={{
            display: "inline-block",
            marginLeft: "12px",
            width: "200px",
            fontWeight: "600",
            textTransform: "capitalize"
          }}
        >
          {channel}{" "}
        </span>
        <span>{i18next.t("A campaign manager performs the update manually")}</span>
      </>
    );
  }
};
const ModalContent = ({
  isClientView,
  platformAdId,
  tbCampaignId,
  adsetId,
  channel,
  destinationLink,
  channelCampaignId,
  fbAccountId,
  selectedPostId,
  selectedPostNumber,
  previousUrl,
  updateChannelData
}) => {
  logger.debug("ChannelLinkEditor:ModalContent", "called", {
    platformAdId,
    campaignId: tbCampaignId,
    channel,
    destinationLink,
    selectedPostId,
    selectedPostNumber,
    previousUrl
  });
  const { t } = useTranslation();

  const [isUpdatingDestinationLink, setIsUpdatingDestinationLink] = useState(false);
  const [updateResults, setUpdateResults] = useState();

  useEffect(() => {
    const ref = Actions.updateDestinationLink.completed.listen(({ payload }) => {
      logger.log("ChannelLinkEditor:ModalContent", "finished updating the destination link", {
        payload,
        campaignId: tbCampaignId,
        channel
      });
      const channelResults = _.find(payload, { channel });
      setUpdateResults(channelResults);
      setIsUpdatingDestinationLink(false);
    });

    const refFailed = Actions.updateDestinationLink.failed.listen((error) => {
      logger.log("error:ChannelLinkEditor:ModalContent", "unable to update destination link", {
        error,
        campaignId: tbCampaignId
      });
      Actions.addNotification(
        t("URL cannot be updated. Please update the URL manually on the channels"),
        "error",
        true
      );
      setUpdateResults({
        status: "FAILED",
        message: error?.message || t("The request to update the URL failed")
      });
      setIsUpdatingDestinationLink(false);
      // Revert back if update did not go through
      updateChannelData({ key: "url", value: previousUrl });
    });

    setIsUpdatingDestinationLink(true);

    const updates = [
      {
        channel,
        destinationLink,
        postNumber: selectedPostNumber
      }
    ];

    Actions.updateDestinationLink(tbCampaignId, adsetId, updates);

    return () => {
      ref();
      refFailed();
    };
  }, [
    platformAdId,
    tbCampaignId,
    channel,
    destinationLink,
    selectedPostId,
    selectedPostNumber,
    adsetId
  ]);
  if (!CHANNELS_WITH_AUTO_LINK_UPDATES.includes(channel)) {
    return <div style={{ margin: "0 40px 40px" }}>{t("The Platform Ad cannot be updated")}</div>;
  }

  return (
    <div style={{ margin: "0 40px 40px" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {isUpdatingDestinationLink ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <LoadingIndicator />
              <span style={{ marginLeft: "12px", fontWeight: "bold", textTransform: "capitalize" }}>
                {channel}{" "}
              </span>
            </div>
          ) : (
            parseResultToMessage({
              ...(updateResults || {}),
              isClientView,
              channelCampaignId,
              channel,
              fbAccountId
            })
          )}
        </div>
      </div>
    </div>
  );
};

//------------------------------------------------------------------------------
// Main Functional Component
//------------------------------------------------------------------------------
const CampaignCreateChannelLinkEditor = ({
  isClientView,
  channelLink,
  adsetId,
  campaignId,
  updateChannelData,
  selectedPreviewChannel,
  campaignData,
  editable,
  hasAts,
  clientView,
  platformAdId,
  selectedPostId,
  selectedPostNumber
}) => {
  // Defines use transaltion object
  const { t } = useTranslation();

  logger.debug("ChannelLinkEditor", "called", {
    channelLink,
    campaignId,
    updateChannelData,
    selectedPreviewChannel,
    campaignData,
    editable,
    clientView,
    platformAdId
  });
  const [showModal, setShowModal] = useState(false);
  const [destinationLink, setDestinationLink] = useState(channelLink);
  const channelCampaignId = useMemo(
    () => campaignData[channelsFieldsByShortName[selectedPreviewChannel]?.name],
    [campaignData, selectedPreviewChannel]
  );
  const previousUrl = usePrevious(channelLink);
  const handleUpdateChannel = useCallback(
    ({ key, value }) => {
      setDestinationLink(value);
      updateChannelData({ key, value });

      /* ToFix: Right now, campaigns created in Onlyfy don't have the platformAdId added in the DDBB*/
      const isOnlyfyCampaign = campaignData.orgaName === "Onlyfy";

      if (isOnlyfyCampaign) {
        Actions.addNotification(
          t(
            "This campaign does not currently support direct synchronization of the campaign URL. Please contact support."
          ),
          "warning"
        );
        return;
      }

      setShowModal(true);
    },
    [updateChannelData, campaignData]
  );

  return (
    <ChannelLinkEditorWrapper>
      <ChannelDataEditor
        dataKey="url"
        value={channelLink}
        urlValue={channelLink}
        label={t("Destination Link")}
        alias={t("Destination Link")}
        updateChannelData={handleUpdateChannel}
        editable={!hasAts || !isClientView}
      />
      <Modal
        onClose={() => {
          setShowModal(false);
        }}
        headline={t("Link change")}
        isOpen={showModal}
      >
        <ModalContent
          isClientView={isClientView}
          tbCampaignId={campaignData?.id}
          adsetId={adsetId}
          channel={selectedPreviewChannel}
          destinationLink={destinationLink}
          channelCampaignId={channelCampaignId}
          fbAccountId={campaignData?.fbAccountId}
          platformAdId={platformAdId}
          selectedPostId={selectedPostId}
          selectedPostNumber={selectedPostNumber}
          previousUrl={previousUrl}
          updateChannelData={updateChannelData}
        />
      </Modal>
    </ChannelLinkEditorWrapper>
  );
};

export default withRefluxStore(CampaignCreateChannelLinkEditor, [PreviewStore], ["campaignData"]);
